import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { AxiosError } from "axios";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import {
  ChevronRight,
  Eye,
  Compass,
  Link as LinkIcon,
  ArrowUpRight,
  Globe,
  Settings,
  Bell,
  MonitorCheck,
  Headset,
} from "lucide-react";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import Router, { useRouter } from "next/router";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { BiSolidHelpCircle, BiSupport } from "react-icons/bi";
import { BsGlobe } from "react-icons/bs";
import { FaAngleRight, FaBell, FaLink } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { RiSettings5Fill } from "react-icons/ri";
import { useRecoilState } from "recoil";
import { useQuickStrategyContext } from "@/contexts/QuickStrategyContext";
import dotenv from "dotenv";
import { IoMdClose } from "react-icons/io";
import { technicalSupportModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useGetLiveDeploymentStats } from "@/api/strategyApi";
import { useUserContext } from "@/contexts/userContext";
import {
  useGetUserAllNotification,
  useGetUserNotification,
  usePostReadDeploymentNotification,
  usePostSetLanguagePref,
} from "@/api/userApi";
import {
  formatDateLocal,
  Pagination,
  useToastHelper,
} from "@/helpers/helperFunctions";
import { headerTradesState } from "@/Atoms/auth/HeaderTrades";
import Banner from "./Banner";
import OnboardingBar from "./OnboardingBar";
import HeaderStats from "./HeaderStats";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import CreateStrategyForm from "../build/CreateStrategyForm";
import Funds from "@/components/Funds";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import TimezoneSelector from "@/components/common/TimezoneSelector";
import Hamburger from "hamburger-react";
import { useSidebarContext } from "@/contexts/SidebarContext";
import Image from "next/image";
import { useStrategyMode } from "@/contexts/StrategyModeContext";
import { useBacktestTour } from "@/contexts/BacktestTourContext";
import PredictionMarketsWelcomeModal from "@/components/common/PredictionMarketsWelcomeModal";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { AiOutlineLink } from "react-icons/ai";
import { Separator } from "@/components/ui/separator";

Chart.register(
  CategoryScale,
  ArcElement,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface IHeaderProps {
  publicMenu?: ReactNode;
  sticky?: boolean;
  blur?: boolean;
  checked?: boolean;
  icons?: boolean;
  hideHeader?: boolean;
  onScrollBg?: boolean;
  showInfo?: boolean;
  toggleUserMenu?: (val: boolean) => void;
  showDisabled?: boolean;
  hideSidebar?: boolean;
}

const NOTIFICATION_LIMIT = 5;

export default function Header(props: IHeaderProps) {
  dotenv.config();

  const { toast } = useToastHelper();
  const { userType } = useSubscription();
  const { changeScannerType, setQuickStrategyModalToggle, openQuickStrategy } =
    useQuickStrategyContext();
  const { user: userInfo } = useUserContext();
  const router = useRouter();
  const { t } = useTranslation("layout-page");
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const [showRedDot, setShowRedDot] = useState<boolean>(false);
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [createdTotalCount, setCreatedTotalCount] = useState(0);
  const [currentCreatedPage, setCurrentCreatedPage] = useState(1);
  const [notification, setNotification] = useState([]);
  const [headerTrades, setHeaderTrades] =
    useRecoilState<any>(headerTradesState);
  const [enabledSwitch, setEnabledSwitch] = useState(router.locale || "en");
  const selectedLanguage = useMemo(
    () => router.locale || enabledSwitch,
    [router.locale, enabledSwitch],
  );
  const { details } = useAppDetails();
  const { newUserSteps } = useBacktestTour();
  const {
    mode,
    setMode,
    welcomeModalOpen,
    setWelcomeModalOpen,
    hasEventAnalyzed,
    predictionSearchValue,
    setPredictionSearchValue,
    triggerPredictionSearch,
    predictionSearchLoading,
  } = useStrategyMode();
  const [showPredictionHint, setShowPredictionHint] = useState(false);
  const [toggleMounted, setToggleMounted] = useState(false);

  useEffect(() => {
    if (router.pathname === "/build" && mode === "equities_fx" && userInfo?.user_id) {
      const isCompleted = newUserSteps >= 4 || newUserSteps === -1;
      if (isCompleted) {
        const storageKey = `welcome_pm_modal_shown_${userInfo.user_id}`;
        const modalShown = localStorage.getItem(storageKey);
        if (!modalShown) {
          setWelcomeModalOpen(true);
        }
      }
    }
  }, [router.pathname, mode, newUserSteps, userInfo?.user_id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setToggleMounted(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (mode === "equities_fx") {
      setShowPredictionHint(true);
      const timer = setTimeout(() => {
        setShowPredictionHint(false);
      }, 4000);
      return () => clearTimeout(timer);
    } else {
      setShowPredictionHint(false);
    }
  }, [mode]);

  const handleModeChange = (
    newMode: "equities_fx" | "prediction_market",
    event: React.MouseEvent,
  ) => {
    // @ts-ignore
    if (!document.startViewTransition) {
      setMode(newMode);
      return;
    }

    // @ts-ignore
    document.startViewTransition(() => {
      flushSync(() => {
        setMode(newMode);
      });
    });
  };

  let [showPopup, setShowPopup] = useState(true);
  const handleViewScanner = () => {
    router.push(`/deploy?source=deploy`);
  };
  const toastMessage = (status: boolean, message: string) => {
    if (status == true) {
      toast({
        title: message,
      });
      return;
    } else {
      toast({
        title: message,
        variant: "destructive",
      });
    }
  };
  const scrollRef = useRef<any>(null);
  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom,
  );

  const {
    data: liveDeploymentData,
    isLoading: liveDataLoading,
    isSuccess: liveDataSuccess,
  } = useGetLiveDeploymentStats(userInfo?.user_id);

  const {
    data: userNotificationData,
    isLoading: notificationLoading,
    isSuccess: notificationSuccess,
    isError: notificationError,
    error: notificationErrorData,
  } = useGetUserNotification(userInfo?.user_id, currentCreatedPage);

  const {
    data: allUserNotificationData,
    isLoading: allNotificationLoading,
    isSuccess: allNotificationSuccess,
    isError: allNotificationError,
    error: allNotificationErrorData,
  } = useGetUserAllNotification(userInfo?.user_id, currentCreatedPage);

  const { mutateAsync: readNotifications } =
    usePostReadDeploymentNotification();

  const {
    mutateAsync: saveLanguagePreference,
    data: saveLanguageData,
    isSuccess: saveLanguageSuccess,
  } = usePostSetLanguagePref();

  const handleNotificationClick = (userInfo: any) => {
    readNotifications(userInfo?.user_id);
  };

  useEffect(() => {
    if (userInfo?.preference?.language) {
      setEnabledSwitch(userInfo?.preference?.language);
    } else {
      setEnabledSwitch("en");
    }
  }, []);

  const saveLanguagePref = async (language: any) => {
    await saveLanguagePreference({
      user_id: userInfo?.user_id,
      language_pref: language,
    });
    localStorage.setItem(
      "user-info",
      JSON.stringify({
        ...userInfo,
        preference: { ...userInfo?.preference, language },
      }),
    );
    setEnabledSwitch(language || "en");
    router.push(router.asPath, undefined, { locale: language });
  };

  const getNameInitals = () => {
    if (
      userInfo &&
      userInfo?.user?.first_name?.length > 0 &&
      userInfo?.user?.first_name != ""
    ) {
      return (
        userInfo?.user.first_name?.[0]?.toUpperCase() +
        (userInfo?.user.last_name?.[0]?.toUpperCase() || "")
      );
    }
    return userInfo?.user?.email?.[0]?.toUpperCase?.() || "#";
  };

  const languagesOptions = useMemo(
    () => [
      { name: t("Bahasa Indonesia"), value: "id" },
      { name: t("Bahasa Malaysia"), value: "ms" },
      { name: t("Chinese"), value: "zh" },
      { name: t("English"), value: "en" },
      { name: t("German"), value: "de" },
      { name: t("Italian"), value: "it" },
      { name: t("Portuguese"), value: "pt" },
      { name: t("Spanish"), value: "es" },
      { name: t("Japanese"), value: "ja" },
      { name: t("Thai"), value: "th" },
      { name: t("Vietnamese"), value: "vi" },
    ],
    [t],
  );

  useEffect(() => {
    if (router?.query?.flag) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      let { flag, ...query } = router.query;
      router.replace({ query }, void 0, { shallow: true });
    }
  }, [router]);

  useEffect(() => {
    if (saveLanguageSuccess && saveLanguageData) {
      toastMessage(true, saveLanguageData?.data.message);
    }
  }, [saveLanguageSuccess, saveLanguageData]);

  useEffect(() => {
    if (liveDataSuccess && liveDeploymentData?.data) {
      const result = {
        ...liveDeploymentData?.data,
        total_strategies: liveDeploymentData.data.total_strategies || 0,
        total_live_strategies:
          liveDeploymentData.data.total_live_strategies || 0,
        total_trades: liveDeploymentData.data.total_trades || 0,
        total_volume: liveDeploymentData.data.total_volume || 0,
        broker_win_p: liveDeploymentData.data.broker_win_p || 0,
        net_cumulative_profit:
          liveDeploymentData.data.net_cumulative_profit || 0,
        total_exposure: liveDeploymentData.data.total_exposure || 0,
        netPosition_broker:
          liveDeploymentData.data.broker_loss != null &&
          liveDeploymentData.data.broker_profit != null
            ? liveDeploymentData.data.broker_profit +
              liveDeploymentData.data.broker_loss
            : 0,
      };

      setHeaderTrades(result);
    }
  }, [liveDataSuccess, liveDataLoading, liveDeploymentData]);

  useEffect(() => {
    if (notificationSuccess && userNotificationData) {
      setNotification(userNotificationData?.data?.items);
      setCreatedTotalCount(userNotificationData?.data?.total);
      setShowRedDot(true);
    } else if (notificationError && notificationErrorData) {
      if (
        (notificationErrorData as AxiosError<{ detail: string }>)?.response
          ?.data?.detail ===
          "Request free access to evaluate or upgrade to the Pro plan for full access." &&
        (window.location.pathname.includes("test") ||
          window.location.pathname.includes("deploy") ||
          window.location.pathname.includes("build"))
      ) {
        router.push("/welcome-tour");
      }
    }
  }, [
    notificationSuccess,
    notificationError,
    notificationLoading,
    userNotificationData,
  ]);

  useEffect(() => {
    if (allNotificationSuccess && allUserNotificationData) {
      const prev = allUserNotificationData?.data?.items.map((item: any) => ({
        strategy_name: item?.strategy.strategy_name,
        deployment_id: item?.id,
        isDeployedAlerts: true,
      }));
    } else if (allNotificationError && allNotificationErrorData) {
      const e = allNotificationErrorData as AxiosError<{ detail: string }>;
      if (
        e?.response?.data?.detail ===
        "Request free access to evaluate or upgrade to the Pro plan for full access."
      ) {
        toastMessage(true, e?.response?.data?.detail);
      }
      if (e?.response?.data?.detail == "Invalid Session. Please Login again.") {
        Router.push({ pathname: "/logout", query: Router.query }).then(() =>
          router.reload(),
        );
      }
      console.error("error get_all_notification_deployments", e?.message);
    }
  }, [
    allNotificationError,
    allNotificationErrorData,
    allNotificationLoading,
    allNotificationSuccess,
  ]);

  return (
    <>
      <div
        id="main-header"
        className={`sm:sticky sm:top-0 z-50 min-h-[4rem] bg-card ${
          props?.onScrollBg && "z-20 bg-card "
        } ${props?.showDisabled ? "pointer-events-none opacity-50" : ""}`}
      >
        <Banner></Banner>
        {/* To show bar only on market place and prediction market pages */}
        {(router?.pathname?.includes("marketplace") ||
          router?.pathname?.includes("prediction-market")) &&
          showPopup && (
            <div className="p-[6px] pl-2 bg-[--accent-primary] text-xs flex justify-between items-center">
              <div>
                {t(
                  " These strategies are for educational purposes only, showcasing what's possible with Level2. Please conduct your own research before making any trading decisions.",
                )}
              </div>
              <div
                onClick={() => setShowPopup(false)}
                className="cursor-pointer h-full bold flex justify-center items-center  pr-1"
              >
                <IoMdClose />
              </div>
            </div>
          )}

        <div className="flex flex-col xl:flex-row justify-between text-sm px-4 py-2">
          {liveDeploymentData?.data?.onboarding_completed ? (
            <HeaderStats headerTrades={liveDeploymentData?.data} />
          ) : (
            <OnboardingBar onboardingData={liveDeploymentData?.data} />
          )}

          <div
            className="flex flex-row grow gap-2 justify-end items-center mr-1 relative sm:mr-1"
            id="notificationMenu"
          >
            <TimezoneSelector />

            <Badge
              onClick={() => {
                setShowTechnicalRequest({
                  isOpen: true,
                  isRequest: false,
                  helpline: true,
                  isContactSales: false,
                });
              }}
              className="cursor-pointer text-center leading-3"
            >
              {t("Technical support")}
            </Badge>
            {!details?.isWhiteLabled && (
              <Link
                href={{
                  pathname: "https://help.trylevel2.com",
                }}
                target="_blank"
                className=" text-foreground rounded-full aspect-square text-primary-foreground p-1"
              >
                <Headset size={16} className="text-foreground" />
              </Link>
            )}
            <Link href={"https://level2.statuspage.io/#"} target="_blank">
              <MonitorCheck size={16}></MonitorCheck>
            </Link>
            {!details?.isWhiteLabled && (
              <div
                className="flex justify-center items-center"
                id="languageMenu"
              >
                <DropdownMenu
                  open={showLanguageMenu}
                  onOpenChange={(open) => setShowLanguageMenu(open)}
                >
                  <DropdownMenuTrigger asChild>
                    <div className="flex flex-col">
                      <Globe
                        className="cursor-pointer text-xs"
                        size={16}
                        onClick={() => {
                          setShowLanguageMenu(true);
                        }}
                      />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-52" sideOffset={20}>
                    <DropdownMenuLabel>
                      {t("Platform Language")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <div className="flex flex-col gap-2 px-2">
                        {languagesOptions.map((lang, index) => {
                          const isSelected = lang.value === selectedLanguage;
                          return (
                            <div
                              className="flex flex-row items-center justify-between gap-2"
                              key={index}
                            >
                              <Label htmlFor={"lang-box-" + lang.value}>
                                {lang.name}
                              </Label>
                              <Switch
                                id={"lang-box-" + lang.value}
                                checked={isSelected}
                                disabled={isSelected}
                                onCheckedChange={() =>
                                  saveLanguagePref(lang.value)
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            {!details?.isWhiteLabled && (
              <Link
                href={{
                  pathname: "/settings",
                  query: { locale: router.locale || "en" },
                }}
                className="flex justify-center items-center"
              >
                <div
                  title={t("Setting and preferences")}
                  className="flex justify-center items-center text-sm text-center relative py-2 cursor-pointer rounded-full"
                >
                  <Settings className="text-base" size={16} />
                </div>
              </Link>
            )}
            <Popover
              open={showNotificationMenu}
              onOpenChange={(open) => setShowNotificationMenu(open)}
            >
              <PopoverTrigger asChild>
                <div
                  title={t("header.iconTitle.notification")}
                  className="flex justify-center items-center text-sm cursor-pointer relative"
                  onClick={() => handleNotificationClick(userInfo)}
                >
                  <Bell size={16} />
                  {notification.length > 0 && showRedDot && (
                    <div className="rounded-xl absolute bottom-[10px] left-[10px]">
                      <div className="w-[10px] h-[10px] rounded-xl  bg-[#FF0000]"></div>
                    </div>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-screen sm:max-w-lg">
                <div
                  className="flex w-full justify-end mb-2"
                  onClick={() => setShowNotificationMenu(false)}
                >
                  <Button size="icon" variant="ghost">
                    <MdClose />
                  </Button>
                </div>
                <Tabs defaultValue="deployed-alerts">
                  <TabsList className="w-full">
                    <TabsTrigger value="deployed-alerts" className="grow">
                      {"Deployed Scanner Alerts"}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="deployed-alerts">
                    <Table>
                      <TableCaption className="text-sm text-left my-2">
                        <p>
                          {"View alerts generated by your deployed scanners."}{" "}
                          <Link
                            href="/deploy?source=deploy"
                            className="underline"
                          >
                            {"Click here"}
                          </Link>{" "}
                          {"to manage scanners"}
                        </p>
                      </TableCaption>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("Notifications")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {!notification.length && (
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              className="text-center text-xs text-muted-foreground"
                            >
                              {t("No alerts found")}
                            </TableCell>
                          </TableRow>
                        )}
                        {
                          <ScrollArea className="h-[300px] w-full">
                            {notification.map((item: any, index: number) => (
                              <TableRow
                                key={index}
                                className="flex w-full justify-between items-start"
                              >
                                <TableCell className="flex justify-between items-start w-full gap-2">
                                  <div className="flex flex-col gap-2 w-full">
                                    <p className="text-muted-foreground">
                                      {
                                        item?.scanner_deployment?.strategy
                                          ?.strategy_name
                                      }
                                    </p>
                                    {item?.meta?.length > 0 ? (
                                      <p className="px-2">
                                        {item?.meta?.map((meta: string) => (
                                          <p>{meta}</p>
                                        ))}
                                      </p>
                                    ) : (
                                      <p className="px-2">
                                        {t("No alerts generated")}
                                      </p>
                                    )}
                                  </div>
                                  <p className="text-muted-foreground text-xs whitespace-nowrap">
                                    {formatDateLocal(
                                      item?.created_at,
                                      "DD-MMM-YY HH:mm",
                                    )}
                                  </p>
                                  <Button
                                    variant="ghost"
                                    onClick={handleViewScanner}
                                    className="my-auto"
                                  >
                                    <div className="flex text-muted-foreground w-full items-center gap-2">
                                      <p className="text-xs">{t("View")}</p>
                                      <ChevronRight />
                                    </div>
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </ScrollArea>
                        }
                      </TableBody>
                    </Table>
                    <Pagination
                      totalCount={createdTotalCount}
                      currentPage={currentCreatedPage}
                      changePage={setCurrentCreatedPage}
                      limit={NOTIFICATION_LIMIT}
                    />
                  </TabsContent>
                </Tabs>
              </PopoverContent>
            </Popover>

            <Label
              htmlFor="profile-image-upload-input"
              className="cursor-pointer"
            >
              <a href={"/profile/" + userInfo?.user?.unique_key}>
                <Avatar className="rounded-full">
                  <AvatarImage
                    src={userInfo?.user?.profile_image}
                    id="userImage"
                    alt="userImage"
                  />
                  <AvatarFallback>{getNameInitals()}</AvatarFallback>
                </Avatar>
              </a>
            </Label>
          </div>
        </div>
        {!props.hideHeader && (
          <div className="3xs:relative relative md:initial lg:initial xl:initial 2xl:initial">
            <div className="flex-grow z-index-9999">
              <div className="flex items-center">
                <div className="mx-auto w-full py-1 ">
                  <div className="flex flex-row justify-between items-center px-3">
                    {/* Header brand logo start*/}
                    <div className="flex flex-row items-center gap-2 md:px-8 px-0">
                      {!props.hideSidebar && (
                        <div className="lg:hidden flex items-center">
                          <Button
                            onClick={() => setShowSidebar(!showSidebar)}
                            variant="outline"
                            className="px-0 flex items-center justify-center"
                          >
                            <Hamburger
                              size={15}
                              toggled={showSidebar}
                              toggle={setShowSidebar}
                            />
                          </Button>
                        </div>
                      )}
                      <div className={cn("flex-shrink-0 3xs:max-w-[250px]", showSidebar && "lg:hidden")}>
                        <Link
                          href={{
                            pathname: "/build",
                            query: { locale: router.locale || "en" },
                          }}
                        >
                          <img
                            src={details?.brokerLogo}
                            className="md:max-h-[30px] xs:max-h-[25px] max-h-[25px] sm:max-w-[300px]"
                            alt=""
                          />
                        </Link>
                      </div>
                      {!details?.isWhiteLabled && (
                        <Badge className={cn("ml-2 text-xs", showSidebar && "lg:hidden")}>
                          <a href="/subscription">
                            {userType === "Pro" ? "Pro" : userType === "Free_access" ? "Free Access" : "Free"}
                          </a>
                        </Badge>
                      )}
                      <div className="hidden lg:flex lg:flex-row lg:flex-grow-1 md:px-5 px-3"></div>
                    </div>

                    <div className="hidden lg:block">
                      {mode === "prediction_market" ? (
                        hasEventAnalyzed ? (
                          <div className="w-96 max-w-2xl relative">
                            <AiOutlineLink className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <Input
                              type="text"
                              value={predictionSearchValue}
                              onChange={(e) =>
                                setPredictionSearchValue(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  triggerPredictionSearch();
                              }}
                              placeholder={t("Paste market link here")}
                              className="h-9 pr-24 pl-9 bg-slate-900 border-slate-800 focus:border-[--accent-primary] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none shadow-none text-xs rounded-lg text-white"
                            />
                            <Button
                              onClick={() => triggerPredictionSearch()}
                              disabled={
                                !predictionSearchValue ||
                                predictionSearchLoading
                              }
                              loading={predictionSearchLoading}
                              variant="default"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-3 bg-[--accent-primary] hover:bg-[--accent-primary] text-white text-[10px] font-semibold rounded-md flex items-center gap-1"
                            >
                              {t("Analyze")}
                              <FaAngleRight className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : null
                      ) : (
                        <Popover
                          open={openQuickStrategy}
                          onOpenChange={(isOpen) => {
                            setQuickStrategyModalToggle(isOpen); // Dynamically toggle openQuickStrategy
                            if (!isOpen) {
                              changeScannerType(false); // Reset scanner type when closing
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={openQuickStrategy}
                              onClick={() => setQuickStrategyModalToggle(true)} // Open the modal
                              className="lg:w-96 max-w-2xl justify-between text-sm font-semibold text-[--accent-primary] border-[--accent-primary]"
                            >
                              {t("Quick create - Search by ticker symbol")}
                              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="lg:w-96 max-w-2xl p-0">
                            <div className={"w-full"}>
                              <CreateStrategyForm disableBanner />
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>

                    {/* Header brand logo end */}
                    <div className="flex flex-row items-center gap-4 max-w-none w-auto shrink-0">
                      {/* Strategy Mode Switcher with sliding animation */}
                      {!router.pathname.includes("deploy-live") &&
                        !router.pathname.includes("strategy_details") &&
                        !router.pathname.includes("forward-test-performance") && (
                          <>
                            <div className="hidden   lg:flex flex-col items-center justify-start gap-1 h-[40px] select-none">
                            <div className="flex items-center gap-1.5 h-6">
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleModeChange("equities_fx", e)
                                }
                                className={cn(
                                  "relative px-2 py-0.5 text-[10px] font-semibold rounded-md transition-colors duration-200 z-10 tracking-wider",
                                  mode === "equities_fx"
                                    ? "elite-gold-text font-bold"
                                    : "text-slate-400 hover:text-slate-200",
                                )}
                              >
                                {t("Exchange")}
                              </button>

                              <div
                                onClick={(e) =>
                                  handleModeChange(
                                    mode === "equities_fx"
                                      ? "prediction_market"
                                      : "equities_fx",
                                    e,
                                  )
                                }
                                className="w-8 h-4 bg-slate-800 rounded-full p-0.5 cursor-pointer flex items-center relative transition-all duration-300"
                              >
                                <motion.div
                                  className="w-3 h-3 rounded-full shadow-md shadow-[#bf953f]/40 elite-gold-gradient"
                                  initial={false}
                                  animate={{
                                    x: mode === "equities_fx" ? 0 : 16,
                                  }}
                                  transition={
                                    toggleMounted
                                      ? {
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                        }
                                      : { duration: 0 }
                                  }
                                />
                              </div>

                              <button
                                type="button"
                                onClick={(e) =>
                                  handleModeChange("prediction_market", e)
                                }
                                className={cn(
                                  "relative px-2 py-0.5 text-[10px] font-semibold rounded-md transition-all duration-200 z-10 tracking-wider",
                                  mode === "prediction_market"
                                    ? "elite-gold-text font-bold"
                                    : "text-slate-400 hover:text-slate-200",
                                )}
                              >
                                {t("Events")}
                                {mode !== "prediction_market" && (
                                  <span className="absolute -top-2 -right-4 px-1 py-0.5 text-[6px] font-black uppercase elite-gold-gradient text-black rounded-[3px] leading-none tracking-normal scale-90 border border-yellow-500/20 shadow-sm shadow-[#bf953f]/20 ">
                                    {t("New")}
                                  </span>
                                )}
                              </button>
                            </div>
                            <span className="text-[8px] text-slate-300 font-bold tracking-wider px-2 py-0.5 rounded bg-slate-900 border border-slate-800 leading-none scale-90 origin-top">
                              {mode === "equities_fx"
                                ? t("Active: Equities, Forex, Crypto")
                                : t("Active: Prediction Markets")}
                            </span>
                          </div>
                          <Separator
                            orientation="vertical"
                            className="h-6 bg-muted-foreground/25"
                          />
                        </>
                      )}
                      {/* Funds block start */}
                      <div
                        id="user-broker-funds"
                        className="w-[180px] shrink-0 flex justify-start"
                      >
                        <div className="w-full flex justify-start">
                          <Funds />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* <PredictionMarketsWelcomeModal
        isOpen={welcomeModalOpen}
        onClose={() => setWelcomeModalOpen(false)}
      /> */}
    </>
  );
}
